import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertCircle, ArrowRight, KeyRound, Sparkles } from 'lucide-react';
import { Alert, AlertDescription } from '@reflex/ui/components/ui/alert';
import { Card } from '@reflex/ui/components/ui/card';
import { PanelTitle, PageDescription, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { api } from '@/lib/api';
import { orgSetupApi } from '@/lib/org-setup-api';
import { queryKeys } from '@/lib/query-keys';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { RunloopConnectedAccount } from '@/components/shared/RunloopConnectedAccount';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { SecretInputDialog } from '@/components/forms/SecretInputDialog';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { MissingSecretsBanner } from './MissingSecretsBanner';
import { IntegrationSecretGroup } from './IntegrationSecretGroup';
import { CustomSecretsSection } from './CustomSecretsSection';
import { SecretManagerCard } from './SecretManagerCard';
import type { SecretStatusItem } from '@/lib/types';

export function AdminSecretsPage({ embedded = false }: { embedded?: boolean }) {
  const queryClient = useQueryClient();
  const routes = useOrgRoutes();
  const { activeOrganizationId } = useOrganization();
  const statusQuery = useQuery({
    queryKey: queryKeys.secretsStatus,
    queryFn: api.getSecretsStatus,
  });

  const sandboxQueryKey = ['org-setup', activeOrganizationId, 'sandbox-provider'] as const;
  const sandboxQuery = useQuery({
    queryKey: sandboxQueryKey,
    queryFn: () => orgSetupApi.getSandboxProvider(activeOrganizationId!),
    enabled: !!activeOrganizationId,
  });
  const runloopAccount = sandboxQuery.data?.account ?? null;

  // Org-tier secrets are saved/deleted through the org `/admin/secrets/*`
  // routes. Service-tier credentials (sign-in providers, GitHub App) live
  // exclusively on the Service Secrets page — we filter them out below
  // so they never reach this page's mutations.
  const setMutation = useMutation({
    mutationFn: ({ name, value }: { name: string; value: string }) => api.setSecret(name, value),
    onSuccess: () => {
      // Prefix-match invalidation hits every org's cached secrets list and
      // discovery query in one shot; secret rotation can affect every
      // org-scoped picker that's currently mounted.
      queryClient.invalidateQueries({ queryKey: ['secrets'] });
      queryClient.invalidateQueries({ queryKey: sandboxQueryKey });
      toast.success('Secret saved');
      setSaveError(null);
      setEditingSecret(null);
    },
    onError: (err: Error, variables) => {
      setSaveError({ secretKey: variables.name, message: err.message });
      toast.error('Failed to save secret', { description: err.message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => api.deleteSecret(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secrets'] });
      toast.success('Secret deleted');
    },
    onError: (err: Error) => toast.error('Failed to delete secret', { description: err.message }),
  });

  const [editingSecret, setEditingSecret] = useState<SecretStatusItem | null>(null);
  const [saveError, setSaveError] = useState<{ secretKey: string; message: string } | null>(null);

  const data = statusQuery.data;
  const coreIntegrations = data?.integrations.filter((i) => i.category === 'core') ?? [];
  const externalIntegrations = data?.integrations.filter((i) => i.category === 'integration') ?? [];
  // Service-tier credentials (sign-in provider OAuth apps, GitHub App)
  // are owned by the Service Secrets page; surface only in the missing
  // banner here so admins still see "N missing across M integrations"
  // include them, but don't render their cards inline.
  const orgScopedIntegrations = data?.integrations.filter((i) => i.category !== 'service') ?? [];
  const customSecrets = data?.customSecrets ?? [];

  const isPending = setMutation.isPending || deleteMutation.isPending;

  const body = (
    <>
      {!embedded && (
        <PageHeader
          iconVariant="box"
          icon={<KeyRound className="h-5 w-5 text-muted-foreground" />}
          title="Secrets & API Keys"
          description="Manage credentials for LLM providers, integrations, and custom services. Secrets are encrypted at rest and injected into devboxes."
          docs="administration/security#secrets-header"
          actions={<SecretManagerCard />}
        />
      )}
      <AsyncContent
        isLoading={statusQuery.isLoading}
        isError={statusQuery.isError}
        loadingMessage="Loading secrets…"
        errorMessage="Failed to load secrets status."
      >
        <div className="space-y-8">
          {data && <MissingSecretsBanner integrations={orgScopedIntegrations} />}

          {!embedded && (
            <Card className="overflow-hidden p-0">
              <Link
                to={routes.credentialsHubSection('organization', 'model-providers')}
                className="flex items-center justify-between gap-3 px-4 py-3 text-sm hover:bg-accent/40"
                data-testid="admin-secrets-model-providers-link"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Sparkles className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <PanelTitle as="span" size="sm">
                      Model Providers
                    </PanelTitle>
                    <PageDescription className="mt-0 text-xs">
                      Manage org-wide Anthropic, OpenAI, Google, Fireworks AI, Baseten, and xAI keys
                      on the dedicated Model Providers page.
                    </PageDescription>
                  </div>
                </div>
                <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </Link>
            </Card>
          )}

          {coreIntegrations.length > 0 && (
            <section>
              <SectionLabel className="mb-3">Core Services</SectionLabel>
              <div className="space-y-3">
                {coreIntegrations.map((integration) => {
                  const isRunloop = integration.secrets.some((s) => s.key === 'RUNLOOP_API_KEY');
                  return (
                    <IntegrationSecretGroup
                      key={integration.id}
                      integration={integration}
                      onEditSecret={(item) => {
                        setSaveError(null);
                        setEditingSecret(item);
                      }}
                      isPending={isPending}
                      defaultOpen={!integration.ready}
                      description={
                        isRunloop ? (
                          <span data-testid="admin-secrets-runloop-account">
                            <RunloopConnectedAccount account={runloopAccount} />
                            {saveError?.secretKey === 'RUNLOOP_API_KEY' &&
                            editingSecret?.key !== 'RUNLOOP_API_KEY' ? (
                              <Alert variant="destructive" className="mt-2">
                                <AlertCircle className="size-4" />
                                <AlertDescription>{saveError.message}</AlertDescription>
                              </Alert>
                            ) : null}
                          </span>
                        ) : undefined
                      }
                    />
                  );
                })}
              </div>
            </section>
          )}

          {externalIntegrations.length > 0 && (
            <section>
              <SectionLabel className="mb-3">Integrations</SectionLabel>
              <div className="space-y-3">
                {externalIntegrations.map((integration) => (
                  <IntegrationSecretGroup
                    key={integration.id}
                    integration={integration}
                    onEditSecret={(item) => {
                      setSaveError(null);
                      setEditingSecret(item);
                    }}
                    isPending={isPending}
                    defaultOpen={!integration.ready}
                  />
                ))}
              </div>
            </section>
          )}

          <CustomSecretsSection
            secrets={customSecrets}
            onUpdateSecret={async (key, value) => {
              await setMutation.mutateAsync({ name: key, value });
            }}
            onDeleteSecret={(key) => deleteMutation.mutate(key)}
            onAddSecret={(name, value) => setMutation.mutate({ name, value })}
            editErrorMessage={(key) => (saveError?.secretKey === key ? saveError.message : null)}
            onEditDialogOpenChange={(open) => {
              if (!open) setSaveError(null);
            }}
            isPending={isPending}
          />
        </div>
      </AsyncContent>

      {editingSecret && (
        <SecretInputDialog
          open={!!editingSecret}
          onOpenChange={(open) => {
            if (!open) {
              setSaveError(null);
              setEditingSecret(null);
            }
          }}
          secretKey={editingSecret.key}
          label={editingSecret.label}
          description={editingSecret.description}
          validationHint={undefined}
          docsUrl={editingSecret.docsUrl}
          isUpdate={editingSecret.isSet}
          sensitive={editingSecret.sensitive !== false}
          errorMessage={saveError?.secretKey === editingSecret.key ? saveError.message : null}
          infoSlot={
            editingSecret.key === 'RUNLOOP_API_KEY' && runloopAccount ? (
              <RunloopConnectedAccount account={runloopAccount} variant="block" />
            ) : undefined
          }
          onSubmit={(value) =>
            setMutation.mutate({
              name: editingSecret.key,
              value,
            })
          }
          isPending={setMutation.isPending}
        />
      )}
    </>
  );

  if (embedded) return body;

  return <PageLayout maxWidth="4xl">{body}</PageLayout>;
}
